// react
import { useEffect, useRef } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// shared components
import { darkTheme, lightTheme } from '@/components/highcharts/themes';

// components
import useSearchParams from '@/hooks/useStableSearchParams';
import { formatNumber } from '@/utils/formatters';

// relative
import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

/**
 * CompressedAirConsumptionChart - Specialized chart for compressed air consumption
 * Shows 2 column series (volume, energy), 1 line series (efficiency), and auto-calculated bands
 *
 * Props:
 * - columnSeries: Array of column series [{ name, data, color }]
 * - lineSeries: Array of line series [{ name, data, color, yAxis }]
 * - visibleData: Array of data points in visible range for band calculation
 * - height: Chart height in pixels (default: 600)
 * - isLoading: Show loading indicator
 */
const CompressedAirConsumptionChart = ({
  columnSeries = [],
  lineSeries = [],
  height = 600,
  isLoading = false,
}) => {
  const chartRef = useRef(null);
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const aggregate = searchParams.get('agg') || 'day';
  const { t } = useTranslation('dashboards-compressed-air-basic');

  // Calculate bands from visible data

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const themeOptions =
      theme.palette?.mode === 'dark' ? darkTheme : lightTheme;

    chart.update(
      {
        chart: { backgroundColor: themeOptions.chart.backgroundColor },
        title: { style: { color: themeOptions.title.style.color } },
        xAxis: {
          lineColor: themeOptions.xAxis.lineColor,
          labels: { style: { color: themeOptions.xAxis.labels.style.color } },
        },
        yAxis: [
          {
            labels: { style: { color: themeOptions.yAxis.labels.style.color } },
            title: {
              style: themeOptions.yAxis.title?.style || {
                color: themeOptions.yAxis.title?.style?.color,
              },
            },
          },
          {
            labels: { style: { color: themeOptions.yAxis.labels.style.color } },
            title: {
              style: themeOptions.yAxis.title?.style || {
                color: themeOptions.yAxis.title?.style?.color,
              },
            },
          },
        ],
        legend: { itemStyle: { color: themeOptions.legend.itemStyle.color } },
        tooltip: {
          backgroundColor: themeOptions.tooltip.backgroundColor,
          borderColor: themeOptions.tooltip.borderColor,
          style: { color: theme.palette.mode === 'dark' ? 'white' : 'black' },
        },
      },
      false,
    );
    chart.redraw();
  }, [theme]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;
    if (isLoading) chart.showLoading('Loading data...');
    else chart.hideLoading();
  }, [isLoading]);

  const options = {
    time: { timezone: 'Europe/Copenhagen' },
    chart: {
      type: aggregate === 'hour' ? 'line' : 'column',
    },
    title: { text: '' },
    xAxis: {
      type: 'datetime',
      crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
      plotLines: [],
    },
    yAxis: [
      {
        // Left Y-axis for volume and energy
        min: 0,
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        title: { text: t('chart.consumptionAxis') },
        opposite: false,
      },
      {
        // Right Y-axis for efficiency
        min: 0,
        title: { text: t('chart.efficiencyAxis') },
        opposite: true,
      },
    ],
    plotOptions: {
      column: {
        stacking: undefined,
        borderColor: 'transparent',
        grouping: true,
        shadow: false,
      },
      line: {
        marker: {
          enabled: false,
        },
      },
      area: {
        marker: {
          enabled: false,
        },
      },
    },
    tooltip: {
      shared: true,
      crosshairs: true,
      headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
      pointFormatter: function () {
        const suffix = this.series.userOptions.yAxis === 1 ? ' %' : '';
        return `<span style="color:${this.color}">\u25CF</span> ${this.series.name}: <b>${formatNumber(this.y, 'da-DK')}${suffix}</b><br/>`;
      },
      xDateFormat: aggregate === 'hour' ? '%d-%m-%Y %H:%M' : '%d-%m-%Y',
    },
    legend: {
      enabled: true,
    },
    series: [
      // Column series
      ...columnSeries.map((s) => ({
        name: s.name,
        data: s.data || [],
        type: aggregate === 'hour' ? 'area' : 'column',
        color: s.color,
        yAxis: 0,
      })),
      // Line series
      ...lineSeries.map((s) => ({
        name: s.name,
        data: s.data || [],
        type: 'line',
        color: s.color,
        yAxis: s.yAxis || 1,
        marker: {
          enabled: false,
        },
      })),
    ],
    accessibility: {
      enabled: false,
    },
  };

  return (
    <div style={{ width: '100%' }}>
      <HighchartsReact
        highcharts={Highcharts}
        containerProps={{ style: { height } }}
        options={options}
        ref={chartRef}
      />
    </div>
  );
};

export default CompressedAirConsumptionChart;
